<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index" v-model="value[index]">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="routeLink(route)"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta?.icon"
                aria-hidden="true"
                :name="route.meta?.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold w-full">
                <span>{{ t(route.displayName) }}</span>
                <VaIcon
                  v-if="route.children"
                  :name="arrowDirection(isCollapsed)"
                  size="20px"
                  class="flex-shrink-0 cursor-pointer"
                />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <VaSidebarItem
            v-for="(childRoute, index2) in route.children"
            :key="index2"
            :to="{ name: childRoute.name }"
            :active="isActiveChildRoute(childRoute)"
            :active-color="activeColor"
            :text-color="textColor(childRoute)"
            :aria-label="`Visit ${t(route.displayName)}`"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-11">
              <VaSidebarItemTitle class="leading-5 font-semibold">
                {{ t(childRoute.displayName) }}
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const isRouteMatch = (navRoute: INavigationRoute) => route.name === navRoute.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (isRouteMatch(section)) {
        return true
      }

      return Boolean(section.children?.some((child) => isRouteMatch(child)))
    }

    const routeLink = (navRoute: INavigationRoute) =>
      navRoute.children && !navRoute.meta?.allowParentLink ? undefined : { name: navRoute.name }

    const setActiveExpand = () => {
      const newExpandState = navigationRoutes.routes.map((route) => routeHasActiveChild(route))
      const hasMenuChanged = newExpandState.some((isActive, index) => isActive && !value.value[index])
      if (hasMenuChanged || value.value.length === 0) {
        value.value = newExpandState
      }
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      routeLink,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
